/**
 * TravelShareDialog — dialog launched from the Share button in the planner
 * header. Lists existing non-revoked links for this trip (with copy +
 * revoke) and has a "Create link" row with an optional expiration date.
 *
 * No-op when the `share_links` feature is disabled: the planner header
 * hides the button, so this component expects to render behind an
 * `enabled` guard and trusts its caller.
 */

import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Check, Trash2, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useTravelShareLinks } from '../hooks';
import type { TravelShareLinkResponse } from '../api';

interface Props {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function buildShareUrl(shareId: string): string {
  if (typeof window === 'undefined') return `/shared/travel/${shareId}`;
  return `${window.location.origin}/shared/travel/${shareId}`;
}

function formatExpiry(iso: string | null): string {
  if (!iso) return 'No expiry';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'No expiry';
  const now = Date.now();
  if (d.getTime() <= now) return 'Expired';
  return `Expires ${d.toLocaleString()}`;
}

export function TravelShareDialog({ tripId, open, onOpenChange }: Props) {
  const { shareLinks, loading, createShareLink, revokeShareLink, isCreating, isRevoking } =
    useTravelShareLinks(tripId);

  const [expiresInDays, setExpiresInDays] = useState<string>('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const activeLinks = useMemo(() => shareLinks.filter((l) => !l.revoked), [shareLinks]);

  const handleCreate = async () => {
    const days = expiresInDays.trim() === '' ? null : parseInt(expiresInDays, 10);
    let expiresAt: string | null = null;
    if (days != null) {
      if (!Number.isFinite(days) || days <= 0) {
        toast({
          title: 'Invalid expiry',
          description: 'Enter a positive number of days or leave blank for no expiry.',
          variant: 'destructive',
        });
        return;
      }
      const d = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
      expiresAt = d.toISOString();
    }
    try {
      await createShareLink({ expiresAt });
      toast({ title: 'Share link created' });
    } catch (err) {
      toast({
        title: 'Could not create share link',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleCopy = (link: TravelShareLinkResponse) => {
    const url = buildShareUrl(link.shareId);
    void navigator.clipboard.writeText(url).then(() => {
      setCopiedId(link.shareId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleRevoke = async (link: TravelShareLinkResponse) => {
    try {
      await revokeShareLink(link.shareId);
      toast({ title: 'Share link revoked' });
    } catch (err) {
      toast({
        title: 'Could not revoke',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Share this trip</DialogTitle>
          <DialogDescription>
            Read-only public links. Anyone with the URL can view the itinerary until you revoke it
            or the link expires.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="share-expires" className="text-xs">
                Expires in (days)
              </Label>
              <Input
                id="share-expires"
                type="number"
                min="1"
                placeholder="No expiry"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
              />
            </div>
            <Button onClick={handleCreate} disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Creating…
                </>
              ) : (
                'Create link'
              )}
            </Button>
          </div>

          <div className="border-t border-border pt-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
              Active links
            </div>
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : activeLinks.length === 0 ? (
              <div className="text-sm text-muted-foreground italic">
                None yet. Create a link above.
              </div>
            ) : (
              <ul className="space-y-2">
                {activeLinks.map((link) => {
                  const url = buildShareUrl(link.shareId);
                  return (
                    <li
                      key={link.shareId}
                      className="flex flex-col gap-1 border border-border rounded-md p-2 bg-muted/20"
                    >
                      <div className="flex items-center gap-2">
                        <Input
                          readOnly
                          value={url}
                          className="font-mono text-xs"
                          onFocus={(e) => e.currentTarget.select()}
                        />
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleCopy(link)}
                          aria-label="Copy link"
                        >
                          {copiedId === link.shareId ? (
                            <>
                              <Check className="h-3 w-3 mr-1" /> Copied
                            </>
                          ) : (
                            <>
                              <Copy className="h-3 w-3 mr-1" /> Copy
                            </>
                          )}
                        </Button>
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                        <span>{formatExpiry(link.expiresAt)}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-[10px] text-destructive"
                          onClick={() => handleRevoke(link)}
                          disabled={isRevoking}
                          aria-label="Revoke link"
                        >
                          <Trash2 className="h-3 w-3 mr-1" /> Revoke
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
